/**
 * Sim Lab camera framing — fit stage/playBounds into the HUD stage-gap
 * (chrome/lanes/footer stay outside the visible play band).
 */

export type CssRect = {
  left: number;
  top: number;
  width: number;
  height: number;
};

export type GameViewport = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type FrameFit = {
  viewport: GameViewport;
  zoom: number;
  centerX: number;
  centerY: number;
};

/** Map a CSS rect on the canvas into Phaser game-pixel viewport. */
export function cssRectToGameViewport(
  canvas: CssRect,
  gap: CssRect,
  gameW: number,
  gameH: number,
): GameViewport | null {
  if (!(canvas.width > 1) || !(canvas.height > 1)) return null;
  if (!(gap.width > 1) || !(gap.height > 1)) return null;
  const sx = gameW / canvas.width;
  const sy = gameH / canvas.height;
  const x = (gap.left - canvas.left) * sx;
  const y = (gap.top - canvas.top) * sy;
  const width = gap.width * sx;
  const height = gap.height * sy;
  if (!(width > 8) || !(height > 8)) return null;
  return { x, y, width, height };
}

/**
 * Zoom + center so `contentW×contentH` fits inside `viewport` with padding.
 * `padFrac` is relative to the smaller viewport edge (default 4%).
 */
export function fitContentInViewport(
  viewport: GameViewport,
  contentW: number,
  contentH: number,
  centerX: number,
  centerY: number,
  padFrac = 0.04,
): FrameFit {
  const pad = Math.min(viewport.width, viewport.height) * Math.max(0, padFrac);
  const availW = Math.max(16, viewport.width - pad * 2);
  const availH = Math.max(16, viewport.height - pad * 2);
  const zoom = Math.min(availW / Math.max(1, contentW), availH / Math.max(1, contentH));
  return {
    viewport,
    zoom: Math.max(0.05, zoom),
    centerX,
    centerY,
  };
}

/**
 * Inset a playBounds rect by game-pixel margins (UI-safe play area).
 * Never collapses below minSpan on each axis.
 */
export function insetPlayBounds(
  b: { xMin: number; xMax: number; yMin: number; yMax: number },
  inset: { top?: number; bottom?: number; left?: number; right?: number },
  minSpan = 120,
): { xMin: number; xMax: number; yMin: number; yMax: number } {
  const left = Math.max(0, inset.left ?? 0);
  const right = Math.max(0, inset.right ?? 0);
  const top = Math.max(0, inset.top ?? 0);
  const bottom = Math.max(0, inset.bottom ?? 0);
  let xMin = b.xMin + left;
  let xMax = b.xMax - right;
  let yMin = b.yMin + top;
  let yMax = b.yMax - bottom;
  if (xMax - xMin < minSpan) {
    const mid = (b.xMin + b.xMax) / 2;
    xMin = mid - minSpan / 2;
    xMax = mid + minSpan / 2;
  }
  if (yMax - yMin < minSpan) {
    const mid = (b.yMin + b.yMax) / 2;
    yMin = mid - minSpan / 2;
    yMax = mid + minSpan / 2;
  }
  return { xMin, xMax, yMin, yMax };
}
